import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { tenantPlanService, TenantPlan } from '../../../services/tenantPlan.service';
import { toast } from 'react-hot-toast';
import { CalendarIcon, CreditCardIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

export default function CurrentPlan() {
  const navigate = useNavigate();
  const [plan, setPlan] = useState<TenantPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    loadCurrentPlan();
  }, []);

  const loadCurrentPlan = async () => {
    try {
      setLoading(true);
      const data = await tenantPlanService.getCurrentPlan();
      setPlan(data);
    } catch (error: any) {
      toast.error(error.message || 'Failed to load plan');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!plan) return;
    
    const confirmed = window.confirm(
      'Are you sure you want to cancel your plan? You will still have access until the end of your billing period.'
    );
    
    if (!confirmed) return;

    try {
      setCancelling(true);
      await tenantPlanService.cancelTenantPlan(plan._id, false);
      toast.success('Plan cancelled successfully');
      loadCurrentPlan();
    } catch (error: any) {
      toast.error(error.message || 'Failed to cancel plan');
    } finally {
      setCancelling(false);
    }
  };

  const getDaysRemaining = () => {
    if (!plan) return 0;
    const endDate = new Date(plan.endDate);
    const today = new Date();
    const diffTime = endDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-CA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      active: 'text-green-800 bg-green-100',
      trial: 'text-blue-800 bg-blue-100',
      expired: 'text-red-800 bg-red-100',
      suspended: 'text-orange-800 bg-orange-100',
      cancelled: 'text-gray-800 bg-gray-100',
    };
    return colors[status] || 'text-gray-800 bg-gray-100';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-white shadow-sm rounded-lg p-8 text-center">
          <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">No Active Plan</h3>
          <p className="mt-2 text-gray-600">
            You don't have an active subscription. Choose a plan to get started.
          </p>
          <button
            onClick={() => navigate('/plans')}
            className="mt-6 inline-flex items-center px-6 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            View Plans
          </button>
        </div>
      </div>
    );
  }

  const daysRemaining = getDaysRemaining();
  const isExpiringSoon = daysRemaining <= 7 && plan.status === 'active';

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Warning Banner */}
      {isExpiringSoon && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                Your plan expires in {daysRemaining} day{daysRemaining !== 1 ? 's' : ''}. 
                <button
                  onClick={() => navigate('/plans')}
                  className="ml-2 font-medium underline hover:text-yellow-800"
                >
                  Renew now
                </button>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Current Plan Card */}
      <div className="bg-white shadow-sm rounded-lg overflow-hidden">
        <div className="px-6 py-8">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{plan.planName}</h2>
              <p className="mt-1 text-sm text-gray-500">Current Subscription</p>
            </div>
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(plan.status)}`}>
              {plan.isTrial ? 'Trial' : plan.status.charAt(0).toUpperCase() + plan.status.slice(1)}
            </span>
          </div>

          {/* Pricing */}
          <div className="mt-6 flex items-baseline">
            <span className="text-4xl font-bold text-gray-900">
              {formatCurrency(plan.planCost, plan.planCurrency)}
            </span>
            <span className="ml-2 text-xl text-gray-600">
              / {plan.planDuration}
            </span>
          </div>

          {/* Details Grid */}
          <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div className="flex items-start">
              <CalendarIcon className="h-6 w-6 text-gray-400 mt-1" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900">Start Date</p>
                <p className="mt-1 text-sm text-gray-600">{formatDate(plan.startDate)}</p>
              </div>
            </div>

            <div className="flex items-start">
              <CalendarIcon className="h-6 w-6 text-gray-400 mt-1" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900">End Date</p>
                <p className="mt-1 text-sm text-gray-600">{formatDate(plan.endDate)}</p>
                <p className="mt-1 text-xs text-gray-500">{daysRemaining} days remaining</p>
              </div>
            </div>

            <div className="flex items-start">
              <CreditCardIcon className="h-6 w-6 text-gray-400 mt-1" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900">Payment Method</p>
                <p className="mt-1 text-sm text-gray-600 capitalize">{plan.paymentMethod}</p>
              </div>
            </div>

            <div className="flex items-start">
              <CheckCircleIcon className="h-6 w-6 text-gray-400 mt-1" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900">Payment Status</p>
                <p className="mt-1 text-sm text-gray-600 capitalize">{plan.paymentStatus}</p>
              </div>
            </div>
          </div>

          {/* Notes */}
          {plan.notes && (
            <div className="mt-6 p-4 bg-gray-50 rounded-md">
              <p className="text-sm font-medium text-gray-900">Notes</p>
              <p className="mt-1 text-sm text-gray-600">{plan.notes}</p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t border-gray-200">
          <div className="flex space-x-3">
            {plan.status === 'active' && !plan.cancelledAt && (
              <>
                <button
                  onClick={() => navigate('/plans')}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Upgrade Plan
                </button>
                <button
                  onClick={handleCancel}
                  disabled={cancelling}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-red-700 bg-red-100 hover:bg-red-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {cancelling ? 'Cancelling...' : 'Cancel Plan'}
                </button>
              </>
            )}
            {plan.status === 'expired' && (
              <button
                onClick={() => navigate('/plans')}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
              >
                Renew Plan
              </button>
            )}
          </div>
          
          {plan.autoRenew && (
            <span className="text-sm text-gray-600">
              Auto-renew enabled
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
